// Always use the local API route
const BIFROST_API_URL = 'https://bifrost.srcful.dev';

class HttpError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: string
  ) {
    super(message);
    this.name = 'HttpError';
  }
}

interface AuthRequest {
  delegatedKey: string;
}

interface SessionUrlResponse {
  session_url: string;
  session_id: string;
}

interface SessionResponse {
  delegatedKey: string;
  expiresAt: number;
  token: string | null;
}

interface ErrorResponse {
  detail: string;
}

export class BifrostService {
  private currentSessionId: string | null = null;
  private maxRetries = 3;
  private retryDelay = 1000; // 1 second

  private async retryFetch(url: string, options: RequestInit, attempt = 1): Promise<Response> {
    try {
      console.log(`Attempting fetch (${attempt}/${this.maxRetries}):`, url);
      const response = await fetch(url, {
        ...options,
        headers: {
          ...options.headers,
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        console.error(`Fetch attempt ${attempt} failed:`, {
          status: response.status,
          statusText: response.statusText,
          url: response.url
        });

        if (attempt < this.maxRetries) {
          console.log(`Retrying in ${this.retryDelay}ms...`);
          await new Promise(resolve => setTimeout(resolve, this.retryDelay));
          return this.retryFetch(url, options, attempt + 1);
        }
      }

      return response;
    } catch (error) {
      console.error(`Network error on attempt ${attempt}:`, error);
      
      if (attempt < this.maxRetries) {
        console.log(`Retrying after network error in ${this.retryDelay}ms...`);
        await new Promise(resolve => setTimeout(resolve, this.retryDelay));
        return this.retryFetch(url, options, attempt + 1);
      }
      throw error;
    }
  }

  public async createSession(publicKey: string): Promise<string> {
    try {
      if (!publicKey) {
        throw new Error('Public key is required');
      }

      console.log('Creating session for public key:', publicKey);
      const options: RequestInit = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          delegatedKey: publicKey,
        } as AuthRequest),
      };

      const response = await this.retryFetch(`${BIFROST_API_URL}/api/auth`, options);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Session creation failed:', {
          status: response.status,
          statusText: response.statusText,
          error: errorText
        });
        
        try {
          const error: ErrorResponse = JSON.parse(errorText);
          throw new HttpError(response.status, 'Failed to create session', error.detail);
        } catch (e) {
          throw new HttpError(response.status, 'Failed to create session', errorText);
        }
      }

      const data: SessionUrlResponse = await response.json();
      console.log('Session created successfully:', data.session_id);
      this.currentSessionId = data.session_id;
      return this.currentSessionId;
    } catch (error) {
      console.error('Error creating session:', {
        error,
        message: error instanceof Error ? error.message : 'Unknown error',
        stack: error instanceof Error ? error.stack : undefined
      });
      throw error;
    }
  }

  public async getSession(): Promise<SessionResponse> {
    if (!this.currentSessionId) {
      throw new Error('No active session');
    }

    try {
      const response = await this.retryFetch(`${BIFROST_API_URL}/api/auth/${this.currentSessionId}`, {
        method: 'GET'
      });
      
      if (!response.ok) {
        const error: ErrorResponse = await response.json();
        throw new HttpError(response.status, `Failed to get session (${response.status})`, error.detail);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting session:', error);
      throw error;
    }
  }

  public async hasToken(): Promise<boolean> {
    const session = await this.getSession();
    return session.token !== null;
  }

  public getCurrentSessionId(): string | null {
    return this.currentSessionId;
  }

  public clearSession(): void {
    this.currentSessionId = null;
  }
} 